export type Faq = {
  id: string
  category: string
  question: string
  answer: string
}

export type Fact = {
  slug: string
  topic: string
  claim: string
  position: string
  explanation: string
  sources: string[]
  updated: string
}

export const faqCategories = [
  'All',
  'Candidate',
  'Policies',
  'Events',
  'Volunteer',
  'Support',
  'Media',
  'Website',
]

export const faqs: Faq[] = [
  {
    id: 'who-is-the-candidate',
    category: 'Candidate',
    question: 'Who is Adebayo S. Bello?',
    answer:
      'An engineer and industrialist who served two terms as Governor of Osun State between 2015 and 2023, after leading the Nigerian Industrial Development Bank and founding an agro-processing business with 22,000 out-growers. The full biography and a verified timeline are on the candidate page.',
  },
  {
    id: 'party-affiliation',
    category: 'Candidate',
    question: 'Which party is he running under?',
    answer:
      'The candidate is contesting the 2027 presidential election on the platform of a registered political party. Full details, including the party constitution and the campaign’s legal filings, are published in the media centre.',
  },
  {
    id: 'how-are-targets-verified',
    category: 'Policies',
    question: 'How will the published targets be verified?',
    answer:
      'Every target carries a named accountable officer, a date and a data source. At one hundred days and every six months thereafter, an independent auditor reviews progress and the scorecard is published in full, including missed targets.',
  },
  {
    id: 'costing',
    category: 'Policies',
    question: 'Are the policies costed?',
    answer:
      'Yes. Each policy page includes a funding section, and the full cost model is published alongside the manifesto so that it can be independently checked.',
  },
  {
    id: 'attend-event',
    category: 'Events',
    question: 'Do I need to register to attend an event?',
    answer:
      'Rallies are open to the public. Town halls and forums with limited seating require registration, and press events require accreditation. Each event page states which applies.',
  },
  {
    id: 'volunteer-time',
    category: 'Volunteer',
    question: 'How much time does volunteering take?',
    answer:
      'As little as two hours a week. You choose your availability during the application, and your ward coordinator matches you to work that fits it. Digital volunteering can be done entirely remotely.',
  },
  {
    id: 'volunteer-under-18',
    category: 'Volunteer',
    question: 'Can I volunteer if I am under 18?',
    answer:
      'You must be 16 or older to register, and volunteers under 18 are limited to supervised, non-field activities such as digital campaigning and research.',
  },
  {
    id: 'contribution-eligibility',
    category: 'Support',
    question: 'Who may contribute to the campaign?',
    answer:
      'Only eligible Nigerian citizens, in their own name, within the limits set by the Electoral Act. The campaign does not accept anonymous, corporate or foreign contributions.',
  },
  {
    id: 'contribution-receipt',
    category: 'Support',
    question: 'Will I receive a receipt?',
    answer:
      'Yes. Every contribution is receipted with a reference number, and aggregate contribution data is reported as required by law.',
  },
  {
    id: 'media-assets',
    category: 'Media',
    question: 'Can I use the campaign photographs?',
    answer:
      'Official portraits and campaign photographs may be used editorially with credit. Usage guidance is stated on each download card in the media centre.',
  },
  {
    id: 'accessibility',
    category: 'Website',
    question: 'Is this website accessible?',
    answer:
      'The site targets WCAG 2.2 AA. It supports keyboard navigation, visible focus indicators, reduced motion, captions on video and a minimum 4.5:1 text contrast ratio. Report any barrier you encounter through the contact page.',
  },
  {
    id: 'languages',
    category: 'Website',
    question: 'Is the site available in other languages?',
    answer:
      'Core pages are being published in Hausa, Yorùbá, Igbo and Nigerian Pidgin. Translations are reviewed by native speakers before publication rather than generated automatically.',
  },
]

export const factTopics = ['All', 'Record', 'Policy', 'Campaign finance', 'Personal']

export const facts: Fact[] = [
  {
    slug: 'osun-salary-record',
    topic: 'Record',
    claim: 'Claim: “Salaries were still owed when he left office in Osun.”',
    position: 'This is false.',
    explanation:
      'Salaries in Osun State were paid on or before the 25th of each month for 84 consecutive months from March 2016 to the end of the second term. The handover audit, signed by the State Accountant-General and the incoming administration, records no outstanding salary liability.',
    sources: [
      'Osun State Accountant-General Report, 2023',
      'Osun State Handover Audit, June 2023',
    ],
    updated: '10 August 2026',
  },
  {
    slug: 'manifesto-costing',
    topic: 'Policy',
    claim: 'Question: “Is the five million jobs target realistic?”',
    position: 'It is ambitious, and it is costed.',
    explanation:
      'The target depends on twelve manufacturing clusters, MSME credit reaching one million businesses, and exchange-rate predictability. The cost model, assumptions and sensitivity analysis are published with the manifesto so that the arithmetic can be challenged directly.',
    sources: ['Manifesto cost model, Annex B', 'National Bureau of Statistics labour data'],
    updated: '15 August 2026',
  },
  {
    slug: 'foreign-funding',
    topic: 'Campaign finance',
    claim: 'Claim: “The campaign is funded from outside Nigeria.”',
    position: 'This is false.',
    explanation:
      'The campaign accepts contributions only from eligible Nigerian citizens in their own name, within statutory limits. Payment processing blocks non-Nigerian instruments, and aggregate contribution data is reported as required by the Electoral Act.',
    sources: ['Campaign contribution policy', 'Electoral Act 2022, Part V'],
    updated: '02 August 2026',
  },
  {
    slug: 'state-policing-position',
    topic: 'Policy',
    claim: 'Question: “Does state policing mean governors control armed forces?”',
    position: 'No. It means state forces under a binding national standard.',
    explanation:
      'The proposal creates state police forces subject to a national inspectorate that sets recruitment, training, use-of-force and human rights standards, with the power to suspend any force that breaches them. It does not transfer control of the armed forces.',
    sources: ['Security policy paper, section 3', 'Draft constitutional amendment summary'],
    updated: '12 August 2026',
  },
  {
    slug: 'health-claim',
    topic: 'Personal',
    claim: 'Claim: “The candidate is receiving treatment abroad.”',
    position: 'This is false.',
    explanation:
      'The candidate has published a medical fitness certificate issued by a Nigerian teaching hospital and has committed to using Nigerian healthcare facilities throughout the campaign and in office.',
    sources: ['Medical certificate, University College Hospital Ibadan, July 2026'],
    updated: '20 July 2026',
  },
]

export function getFact(slug: string): Fact | undefined {
  return facts.find((fact) => fact.slug === slug)
}
